import { useCallback, useEffect, useState } from "react";

const ACTIVE_KEYBOARD_TABLE_CHANGE_EVENT = "view-table-active-keyboard-change";
let activeKeyboardTableId: string | null = null;

function announceActiveKeyboardTableChange() {
    window.dispatchEvent(new Event(ACTIVE_KEYBOARD_TABLE_CHANGE_EVENT));
}

/**
 * Only one ViewTable on the page answers keyboard shortcuts: the one the
 * pointer or focus entered last, else the first to mount. Call
 * `activateKeyboardShortcutsForTable` when the table is entered.
 */
export function useKeyboardShortcutOwner({
    tableId,
    enableKeyboardShortcuts,
}: {
    tableId: string;
    enableKeyboardShortcuts: boolean;
}) {
    const [isKeyboardShortcutOwner, setIsKeyboardShortcutOwner] =
        useState(false);
    const isKeyboardShortcutsEnabledForTable =
        enableKeyboardShortcuts && isKeyboardShortcutOwner;

    const activateKeyboardShortcutsForTable = useCallback(() => {
        if (!enableKeyboardShortcuts || activeKeyboardTableId === tableId) {
            return;
        }
        activeKeyboardTableId = tableId;
        announceActiveKeyboardTableChange();
    }, [enableKeyboardShortcuts, tableId]);

    useEffect(() => {
        const handleActiveTableChange = () => {
            setIsKeyboardShortcutOwner(activeKeyboardTableId === tableId);
        };

        window.addEventListener(
            ACTIVE_KEYBOARD_TABLE_CHANGE_EVENT,
            handleActiveTableChange,
        );

        if (!enableKeyboardShortcuts) {
            setIsKeyboardShortcutOwner(false);
            if (activeKeyboardTableId === tableId) {
                activeKeyboardTableId = null;
                announceActiveKeyboardTableChange();
            }
            return () => {
                window.removeEventListener(
                    ACTIVE_KEYBOARD_TABLE_CHANGE_EVENT,
                    handleActiveTableChange,
                );
            };
        }

        if (activeKeyboardTableId === null) {
            activeKeyboardTableId = tableId;
            announceActiveKeyboardTableChange();
        } else {
            setIsKeyboardShortcutOwner(activeKeyboardTableId === tableId);
        }

        return () => {
            window.removeEventListener(
                ACTIVE_KEYBOARD_TABLE_CHANGE_EVENT,
                handleActiveTableChange,
            );
            if (activeKeyboardTableId === tableId) {
                activeKeyboardTableId = null;
                announceActiveKeyboardTableChange();
            }
        };
    }, [enableKeyboardShortcuts, tableId]);

    return {
        isKeyboardShortcutsEnabledForTable,
        activateKeyboardShortcutsForTable,
    };
}
